/* ============================================================
 * BuchTeaser – Kompakter Teaser für die Homepage
 * Verlinkt zur /buch Seite
 * ============================================================ */
import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { BookOpen, ArrowRight, Star } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const BOOK_COVER = "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/buch-cover-de_083abd69.png";

export default function BuchTeaser() {
  const { t } = useI18n();
  const { ref, visible } = useInView();

  return (
    <section
      ref={ref}
      id="buch"
      className="py-20 bg-gradient-to-b from-[oklch(0.12_0.02_65)] to-[oklch(0.15_0.02_65)]"
    >
      <div className="container max-w-5xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">

          {/* Left: Book Cover */}
          <div
            className={`flex justify-center transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
            }`}
          >
            <div className="relative">
              <div className="absolute -inset-6 bg-gold/8 rounded-3xl blur-2xl" />
              <img
                src={BOOK_COVER}
                alt="Der AYU – Tradition trifft Genetik"
                className="relative w-72 md:w-80 rounded-lg shadow-2xl hover:scale-[1.02] transition-transform duration-500"
              />
            </div>
          </div>

          {/* Right: Info */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
            }`}
          >
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4 text-gold" />
              <span className="text-sm font-semibold tracking-[0.15em] uppercase text-gold">
                {t("landing.buch.badge")}
              </span>
            </div>

            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.92_0.02_85)] mb-2">
              {t("landing.buch.heading")}
            </h2>
            <p className="font-playfair text-lg italic text-gold mb-4">
              {t("landing.buch.subtitle")}
            </p>

            <p className="text-[oklch(0.65_0.02_85)] leading-relaxed mb-6">
              {t("landing.buch.description")}
            </p>

            {/* Stars */}
            <div className="flex items-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-gold text-gold" />
              ))}
              <span className="ml-2 text-xs text-[oklch(0.55_0.02_85)]">{t("landing.buch.stars")}</span>
            </div>

            {/* Price Tags */}
            <div className="flex items-center gap-4 mb-8">
              <div className="px-4 py-2 rounded-full bg-[oklch(0.2_0.02_65)] border border-gold/15">
                <span className="text-sm text-[oklch(0.6_0.02_85)]">{t("landing.buch.ebookFrom")} </span>
                <span className="font-bold text-gold">19,90&euro;</span>
              </div>
              <div className="px-4 py-2 rounded-full bg-[oklch(0.2_0.02_65)] border border-gold/15">
                <span className="text-sm text-[oklch(0.6_0.02_85)]">{t("landing.buch.print")} </span>
                <span className="font-bold text-gold">24,90&euro;</span>
              </div>
            </div>

            <Link
              href="/buch"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gold text-[oklch(0.15_0.02_65)] font-semibold hover:bg-gold-light transition-all hover:scale-[1.02]"
            >
              {t("landing.buch.cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
